import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { FileText, Download, Clock, AlertTriangle, CheckCircle, XCircle } from "lucide-react";

const Reports = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState("hours");
  const [loading, setLoading] = useState(false);
  const [hoursData, setHoursData] = useState([]);
  const [calloutsData, setCalloutsData] = useState([]);
  const [dropRequests, setDropRequests] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: format(startOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd"),
    end: format(endOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd"),
  });
  const [periodType, setPeriodType] = useState("week");

  useEffect(() => {
    if (activeTab === "hours") {
      fetchHoursReport();
    } else if (activeTab === "callouts") {
      fetchCalloutsReport();
    } else if (activeTab === "drops") {
      fetchDropRequests();
    }
  }, [dateRange, activeTab]);

  const handlePeriodChange = (period) => {
    setPeriodType(period);
    const today = new Date();
    if (period === "week") {
      setDateRange({
        start: format(startOfWeek(today, { weekStartsOn: 0 }), "yyyy-MM-dd"),
        end: format(endOfWeek(today, { weekStartsOn: 0 }), "yyyy-MM-dd"),
      });
    } else if (period === "month") {
      setDateRange({
        start: format(startOfMonth(today), "yyyy-MM-dd"),
        end: format(endOfMonth(today), "yyyy-MM-dd"),
      });
    } else if (period === "custom") {
      // Keep current range for custom
    }
  };

  const fetchHoursReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/hours?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers: getAuthHeaders() }
      );
      setHoursData(response.data);
    } catch (error) {
      console.error("Error fetching hours report:", error);
      toast.error("Failed to load hours report");
    } finally {
      setLoading(false);
    }
  };

  const fetchCalloutsReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/callouts?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers: getAuthHeaders() }
      );
      setCalloutsData(response.data);
    } catch (error) {
      console.error("Error fetching callouts report:", error);
      toast.error("Failed to load callouts report");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/drop-requests`, {
        headers: getAuthHeaders(),
        
      });
      setDropRequests(response.data);
    } catch (error) {
      console.error("Error fetching drop requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDropResponse = async (requestId, accept) => {
    try {
      await axios.put(
        `${API}/drop-requests/${requestId}?accept=${accept}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Drop request ${accept ? "approved" : "rejected"}`);
      fetchDropRequests();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to process request");
    }
  };

  const exportToPDF = async (type) => {
    try {
      toast.info("Generating PDF...");
      const endpoint = type === "hours" ? "reports/hours/export-pdf" : "reports/hours/export-pdf";
      const url = `${API}/${endpoint}?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${type}-report-${dateRange.start}-to-${dateRange.end}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("PDF exported successfully!");
    } catch (error) {
      toast.error("Failed to export PDF");
      console.error(error);
    }
  };

  const exportToExcel = async (type) => {
    try {
      toast.info("Generating Excel...");
      const endpoint = type === "hours" ? "reports/hours/export-excel" : "reports/callouts/export-excel";
      const url = `${API}/${endpoint}?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${type}-report-${dateRange.start}-to-${dateRange.end}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Excel exported successfully!");
    } catch (error) {
      toast.error("Failed to export Excel");
      console.error(error);
    }
  };

  const totalOvertime = hoursData.reduce((sum, row) => sum + (row.overtime_hours || 0), 0);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Reports</h1>
          <p className="text-[#666666]">View and export employee reports</p>
        </div>
      </div>

      {/* Date Range Controls */}
      <Card className="border border-[#E5E5E5] shadow-none">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#666666]">Period</Label>
              <Select value={periodType} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-40 border-[#E5E5E5]" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {periodType === "custom" && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#666666]">Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                    className="border-[#E5E5E5]"
                    data-testid="start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#666666]">End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                    className="border-[#E5E5E5]"
                    data-testid="end-date"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="border-b border-[#E5E5E5] bg-transparent w-full justify-start rounded-none p-0">
          <TabsTrigger
            value="hours"
            className="data-[state=active]:border-b-2 data-[state=active]:border-[#6A1E2E] data-[state=active]:text-[#6A1E2E] rounded-none"
            data-testid="tab-hours"
          >
            <Clock className="w-4 h-4 mr-2" />
            Hours Worked
          </TabsTrigger>
          <TabsTrigger
            value="callouts"
            className="data-[state=active]:border-b-2 data-[state=active]:border-[#6A1E2E] data-[state=active]:text-[#6A1E2E] rounded-none"
            data-testid="tab-callouts"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Callouts
          </TabsTrigger>
          <TabsTrigger
            value="drops"
            className="data-[state=active]:border-b-2 data-[state=active]:border-[#6A1E2E] data-[state=active]:text-[#6A1E2E] rounded-none"
            data-testid="tab-drops"
          >
            <FileText className="w-4 h-4 mr-2" />
            Drop Requests
          </TabsTrigger>
        </TabsList>

        {/* Hours Tab */}
        <TabsContent value="hours" className="mt-6">
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-semibold">Hours Report</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToPDF("hours")}
                  className="border-[#E5E5E5]"
                  data-testid="export-hours-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToExcel("hours")}
                  className="border-[#E5E5E5]"
                  data-testid="export-hours-excel"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Excel
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-[#FAFAFA] rounded-sm border border-[#E5E5E5]">
                  <p className="text-xs uppercase tracking-wider text-[#666666]">Total Employees</p>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{hoursData.length}</p>
                </div>
                <div className="p-4 bg-[#FAFAFA] rounded-sm border border-[#E5E5E5]">
                  <p className="text-xs uppercase tracking-wider text-[#666666]">Total Overtime</p>
                  <p className="text-2xl font-bold text-[#E55934]">{totalOvertime.toFixed(1)} hrs</p>
                </div>
                <div className="p-4 bg-[#FAFAFA] rounded-sm border border-[#E5E5E5]">
                  <p className="text-xs uppercase tracking-wider text-[#666666]">Total Shifts</p>
                  <p className="text-2xl font-bold text-[#6A1E2E]">
                    {hoursData.reduce((sum, row) => sum + (row.shift_count || 0), 0)}
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8 text-[#666666]">Loading...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="table-header-uppercase">Employee</TableHead>
                      <TableHead className="table-header-uppercase text-right">Total Hours</TableHead>
                      <TableHead className="table-header-uppercase text-right">Regular</TableHead>
                      <TableHead className="table-header-uppercase text-right">Overtime</TableHead>
                      <TableHead className="table-header-uppercase text-right">Shifts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hoursData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-[#999999]">
                          No data for selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      hoursData.map((row, idx) => (
                        <TableRow key={idx} data-testid={`hours-row-${idx}`}>
                          <TableCell className="font-medium">{row.employee_name}</TableCell>
                          <TableCell className="text-right">{row.total_hours}</TableCell>
                          <TableCell className="text-right">{row.regular_hours}</TableCell>
                          <TableCell className="text-right">
                            {row.overtime_hours > 0 && (
                              <span className="text-[#E55934] font-semibold">{row.overtime_hours}</span>
                            )}
                            {row.overtime_hours === 0 && "0"}
                          </TableCell>
                          <TableCell className="text-right">{row.shift_count}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Callouts Tab */}
        <TabsContent value="callouts" className="mt-6">
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-semibold">Callouts Report</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToPDF("callouts")}
                  className="border-[#E5E5E5]"
                  data-testid="export-callouts-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToExcel("callouts")}
                  className="border-[#E5E5E5]"
                  data-testid="export-callouts-excel"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Excel
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-[#666666]">Loading...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="table-header-uppercase">Employee</TableHead>
                      <TableHead className="table-header-uppercase">Date</TableHead>
                      <TableHead className="table-header-uppercase">Event</TableHead>
                      <TableHead className="table-header-uppercase">Reason</TableHead>
                      <TableHead className="table-header-uppercase">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calloutsData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-[#999999]">
                          No callouts for selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      calloutsData.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{row.employee_name || "N/A"}</TableCell>
                          <TableCell>{row.shift_date || "N/A"}</TableCell>
                          <TableCell>{row.event_name || "N/A"}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{row.reason}</TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-sm ${
                                row.status === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : row.status === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {row.status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Drop Requests Tab */}
        <TabsContent value="drops" className="mt-6">
          <Card className="border border-[#E5E5E5] shadow-none">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold">Pending Drop Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-[#666666]">Loading...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="table-header-uppercase">Employee</TableHead>
                      <TableHead className="table-header-uppercase">Date</TableHead>
                      <TableHead className="table-header-uppercase">Event</TableHead>
                      <TableHead className="table-header-uppercase">Reason</TableHead>
                      <TableHead className="table-header-uppercase">Status</TableHead>
                      <TableHead className="table-header-uppercase text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dropRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[#999999]">
                          No drop requests
                        </TableCell>
                      </TableRow>
                    ) : (
                      dropRequests.map((req) => (
                        <TableRow key={req.id} data-testid={`drop-request-${req.id}`}>
                          <TableCell className="font-medium">{req.employee_name || "N/A"}</TableCell>
                          <TableCell>{req.shift_date || "N/A"}</TableCell>
                          <TableCell>{req.event_name || "N/A"}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{req.reason}</TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-sm ${
                                req.status === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : req.status === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {req.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {req.status === "pending" && (
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleDropResponse(req.id, true)}
                                  className="bg-green-600 hover:bg-green-700 text-white h-8"
                                  data-testid={`approve-drop-${req.id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDropResponse(req.id, false)}
                                  className="border-red-300 text-red-600 hover:bg-red-50 h-8"
                                  data-testid={`reject-drop-${req.id}`}
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
